/*
Purpose: Advanced Analysis configuration modal for power users.
Audience: Non-technical users configuring complex multi-provider AI scans.
What it does:
- Lets user pick providers/models/services, targets, and custom instructions
- Computes validation and estimated costs before launching an order
- Starts orders/jobs via Cloud Functions and shows initialization status
Interplay:
- Uses centralized AI config (frontend mirror) to present models/targets
- Calls backend createAnalysisOrder (The Operator) to create jobs
- Results are later reviewed in the unified results modal system
*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaTimes, FaRocket, FaSave, FaEye, FaMicrophone, FaRobot, FaExclamationTriangle, FaCar, FaUsers, FaFileAlt, FaPlay } from 'react-icons/fa';
import { SiOpenai, SiAmazonwebservices, SiGoogle } from 'react-icons/si';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../firebase';
import log from '../../utils/logger';
import { useAuth } from '../../hooks/useAuth';
import { getAvailableModels, getScanTierConfig } from '../../config/aiModelsConfig';
import { SingleOrderResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import '../../styles/AdvancedAnalysisModal.css';

const AdvancedAnalysisModal = ({ 
  isOpen, 
  onClose, 
  evidence, 
  casefileId,
  initialTargets = [],
  onAnalysisComplete 
}) => {
  const { user, displayName, requireAuth } = useAuth();
  const [casefileName, setCasefileName] = useState(null);
  
  // Fetch casefile name for breadcrumb context
  useEffect(() => {
    if (casefileId && isOpen) {
      const fetchCasefileName = async () => {
        try {
          const casefileDoc = await getDoc(doc(db, 'casefiles', casefileId));
          if (casefileDoc.exists()) {
            const casefileData = casefileDoc.data();
            setCasefileName(casefileData.name || casefileData.title || 'Untitled Casefile');
          }
        } catch (error) {
          console.error('Error fetching casefile name:', error);
        }
      };
      fetchCasefileName();
    }
  }, [casefileId, isOpen]);
  
  // Provider configurations with placeholders (limit to Vertex for now)
  const providers = useMemo(() => ({
    vertex_ai_vision: {
      id: 'vertex_ai_vision',
      name: 'Google Vertex AI',
      icon: SiGoogle,
      color: '#4285f4',
      description: 'Google\'s advanced AI platform with Gemini models',
      isActive: true,
      models: getAvailableModels(),
      serviceCategories: [
        {
          id: 'vertex_ai_vision',
          name: 'Vertex AI Vision',
          description: 'Google\'s advanced computer vision',
          icon: 'FaEye',
          services: [
            { id: 'object_tracking', name: 'Object Tracking', cost: 0.0015, description: 'Detect and classify objects in images/video' },
            { id: 'text_detection', name: 'Text Detection', cost: 0.001, description: 'Extract text from images and documents' },
            { id: 'activity_recognition', name: 'Activity Recognition', cost: 0.003, description: 'Recognize actions, behaviors, and events' },
            { id: 'content_moderation', name: 'Content Moderation', cost: 0.0012, description: 'Detect inappropriate content' }
          ]
        },
        {
          id: 'vertex_ai_speech',
          name: 'Vertex AI Speech',
          description: 'Advanced speech-to-text processing',
          icon: 'FaMicrophone',
          services: [
            { id: 'transcription', name: 'Transcription', cost: 0.006, description: 'Convert speech to text with timestamps' },
            { id: 'speaker_identification', name: 'Speaker Identification', cost: 0.008, description: 'Identify and separate different speakers' }
          ]
        }
      ]
    },
    google_video_intelligence: {
      id: 'google_video_intelligence',
      name: 'Google Video Intelligence',
      icon: FaEye,
      color: '#0F9D58',
      description: 'Deterministic video analysis (tracking, text-in-video, explicit content)',
      isActive: true,
      models: [{ id: 'video-intelligence-v1', name: 'Video Intelligence v1', speed: 'fast', accuracy: 'high', cost: 0.10 }],
      serviceCategories: [
        {
          id: 'video_primitive_services',
          name: 'Video Primitives',
          description: 'Timestamped detections suitable for forensic use',
          icon: 'FaEye',
          services: [
            { id: 'object_tracking', name: 'Object Tracking', cost: 0.10, description: 'Track objects/people with timestamps' },
            { id: 'text_detection', name: 'Text-in-Video', cost: 0.10, description: 'OCR for video frames with timing' },
            { id: 'person_detection', name: 'Person Detection', cost: 0.10, description: 'Detect people and poses' },
            { id: 'explicit_content', name: 'Explicit Content', cost: 0.10, description: 'Detect explicit segments' },
            { id: 'shot_change', name: 'Shot Changes', cost: 0.05, description: 'Shot/scene change timeline events' }
          ]
        }
      ]
    },
    openai: {
      id: 'openai',
      name: 'OpenAI',
      icon: SiOpenai,
      color: '#00a67e',
      description: '[Integration Coming Soon] OpenAI\'s GPT and vision models',
      isActive: false,
      models: [
        { id: 'gpt-4o', name: 'GPT-4o', cost: 0.005, speed: 'fast', accuracy: 'highest' },
        { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', cost: 0.003, speed: 'fast', accuracy: 'high' },
        { id: 'gpt-4-vision', name: 'GPT-4 Vision', cost: 0.004, speed: 'medium', accuracy: 'highest' }
      ],
      services: [
        { id: 'image_analysis', name: '[Image Analysis]', cost: 0.004, description: '[Advanced image understanding and description]' },
        { id: 'document_analysis', name: '[Document Analysis]', cost: 0.003, description: '[Extract and analyze document content]' },
        { id: 'conversation_analysis', name: '[Conversation Analysis]', cost: 0.002, description: '[Analyze audio conversations and interactions]' }
      ]
    },
    aws_bedrock: {
      id: 'aws_bedrock',
      name: 'AWS Bedrock',
      icon: SiAmazonwebservices,
      color: '#ff9900',
      description: '[Integration Coming Soon] AWS managed AI services',
      isActive: false,
      models: [
        { id: 'claude-3-opus', name: '[Claude 3 Opus]', cost: 0.008, speed: 'slower', accuracy: 'highest' },
        { id: 'claude-3-sonnet', name: '[Claude 3 Sonnet]', cost: 0.004, speed: 'fast', accuracy: 'high' },
        { id: 'titan-image', name: '[Titan Image]', cost: 0.002, speed: 'fast', accuracy: 'good' }
      ],
      services: [
        { id: 'content_analysis', name: '[Content Analysis]', cost: 0.003, description: '[Comprehensive content understanding]' },
        { id: 'entity_extraction', name: '[Entity Extraction]', cost: 0.002, description: '[Extract people, places, organizations]' },
        { id: 'sentiment_analysis', name: '[Sentiment Analysis]', cost: 0.001, description: '[Analyze emotional tone and sentiment]' }
      ]
    }
  }), []);

  // Analysis targets configuration
  const availableTargets = useMemo(() => [
    {
      id: 'vehicles',
      name: 'Vehicles',
      icon: FaCar,
      description: 'Detect & track vehicles; make/model ID; license plates; marks',
      cost: '$0.0015/min'
    },
    {
      id: 'people',
      name: 'People',
      icon: FaUsers,
      description: 'Detect people, facial analysis, behavior tracking',
      cost: '$0.002/min'
    },
    {
      id: 'activities',
      name: 'Activities',
      icon: FaPlay,
      description: 'Recognize actions, behaviors, and events',
      cost: '$0.003/min'
    },
    {
      id: 'text',
      name: 'Text & OCR',
      icon: FaFileAlt,
      description: 'Extract text from images and documents',
      cost: '$0.001/min'
    },
    {
      id: 'audio',
      name: 'Audio Analysis',
      icon: FaMicrophone,
      description: 'Speech transcription and audio analysis',
      cost: '$0.006/min'
    }
  ], []);

  // State management
  const [activeTab, setActiveTab] = useState('vertex_ai_vision');
  const [selectedTargets, setSelectedTargets] = useState(initialTargets.length > 0 ? initialTargets : ['vehicles']);
  const [selectedModels, setSelectedModels] = useState({});
  const [selectedServices, setSelectedServices] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showOrderResults, setShowOrderResults] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [initBusy, setInitBusy] = useState(false);
  const [initMessage, setInitMessage] = useState('');
  const [registrySnapshot, setRegistrySnapshot] = useState([]);
  const [validation, setValidation] = useState({ valid: true, reasons: [] });

  const initializeAiServices = useCallback(async () => {
    setInitBusy(true);
    setInitMessage('');
    try {
      const initFn = httpsCallable(functions, 'initializeServiceRegistry');
      const res = await initFn();
      setInitMessage(res?.data?.message || 'AI services initialized successfully.');
    } catch (e) {
      console.error('Initialize AI Services failed:', e);
      setInitMessage(e?.message || 'Failed to initialize AI services');
    } finally {
      setInitBusy(false);
    }
  }, []);

  // Load service registry once when modal opens
  useEffect(() => {
    const loadRegistry = async () => {
      if (!isOpen) return;
      try {
        const snap = await getDocs(collection(db, 'serviceRegistry'));
        const items = snap.docs.map(d => d.data());
        setRegistrySnapshot(items);
      } catch (e) {
        console.error('Failed to load service registry:', e);
        setRegistrySnapshot([]);
      }
    };
    loadRegistry();
  }, [isOpen]);

  // Validate selections against registry (lightweight, local)
  useEffect(() => {
    if (!isOpen) return;
    const reasons = [];
    const activeServices = registrySnapshot.filter(s => s?.isActive);
    const targets = selectedTargets || [];
    if (targets.length === 0) {
      reasons.push('Select at least one analysis target.');
    }
    // Target coverage: at least one active service supports each selected target
    targets.forEach(t => {
      const supported = activeServices.some(s => Array.isArray(s.supportedTasks) && s.supportedTasks.includes(t));
      if (!supported) {
        reasons.push(`No active AI services currently support target: ${t}.`);
      }
    });
    // File type coverage (best-effort; evidence may have contentType)
    const contentType = Array.isArray(evidence) ? evidence[0]?.contentType : evidence?.contentType;
    if (contentType) {
      const anyFileTypeOK = activeServices.some(s => !Array.isArray(s.supportedFileTypes) || s.supportedFileTypes.includes(contentType));
      if (!anyFileTypeOK) {
        reasons.push(`No active AI services support file type: ${contentType}.`);
      }
    }
    setValidation({ valid: reasons.length === 0, reasons });
  }, [isOpen, registrySnapshot, selectedTargets, evidence]);

  // Toggle target selection
  const toggleTarget = useCallback((targetId) => {
    setSelectedTargets(prev => {
      const newTargets = prev.includes(targetId)
        ? prev.filter(id => id !== targetId)
        : [...prev, targetId];
      return newTargets;
    });
  }, []);

  // Initialize selections
  useEffect(() => {
    if (isOpen) {
      // Default to Vertex AI with some services pre-selected based on targets
      const defaultServices = {};
      if (selectedTargets.includes('vehicles') || selectedTargets.includes('people') || selectedTargets.includes('activities')) {
        defaultServices.object_tracking = true;
      }
      if (selectedTargets.includes('text')) {
        defaultServices.text_detection = true;
      }
      if (selectedTargets.includes('audio')) {
        defaultServices.transcription = true;
        defaultServices.speaker_identification = true;
      }

      setSelectedServices({
        vertex_ai_vision: defaultServices
      });
      setSelectedModels({
        // Default to the cost-effective model for advanced runs unless user changes
        vertex_ai_vision: 'gemini-2.5-flash-lite'
      });
    }
  }, [isOpen, selectedTargets]);

  // Calculate total cost and selections
  const orderSummary = useMemo(() => {
    let totalCost = 0;
    let totalServices = 0;
    let activeProviders = 0;

    Object.entries(selectedServices).forEach(([providerId, services]) => {
      const provider = providers[providerId];
      if (!provider) return;

      const serviceCount = Object.keys(services).filter(serviceId => services[serviceId]).length;
      if (serviceCount > 0) {
        activeProviders++;
        totalServices += serviceCount;
        
        // Calculate cost for this provider
        Object.entries(services).forEach(([serviceId, isSelected]) => {
          if (isSelected) {
            // Search in categorized services if available
            let service = null;
            if (provider.serviceCategories) {
              for (const category of provider.serviceCategories) {
                service = category.services.find(s => s.id === serviceId);
                if (service) break;
              }
            } else if (provider.services) {
              service = provider.services.find(s => s.id === serviceId);
            }
            if (service) totalCost += service.cost;
          }
        });
      }
    });

    return { totalCost, totalServices, activeProviders };
  }, [selectedServices, providers]);

  // Handle service toggle
  const toggleService = useCallback((providerId, serviceId) => {
    setSelectedServices(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        [serviceId]: !prev[providerId]?.[serviceId]
      }
    }));
  }, []);

  // Handle model selection
  const selectModel = useCallback((providerId, modelId) => {
    setSelectedModels(prev => ({
      ...prev,
      [providerId]: modelId
    }));
  }, []);

  // Create analysis order
  const createAnalysisOrder = useCallback(async () => {
    if (!requireAuth()) {
      setError('Please log in to run analysis');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get advanced_scan tier configuration
      const advancedScanConfig = getScanTierConfig('advanced_scan');
      
      // Prepare advanced analysis order configuration
      const orderConfig = {
        scanTier: 'advanced_scan', // NEW: Specify scan tier
        casefileId,
        evidenceIds: Array.isArray(evidence) ? evidence.map(e => e.id) : [evidence.id],
        providers: Object.entries(selectedServices)
          .filter(([_, services]) => Object.values(services).some(Boolean))
          .map(([providerId, services]) => ({
            providerId,
            model: selectedModels[providerId],
            services: Object.entries(services)
              .filter(([_, isSelected]) => isSelected)
              .map(([serviceId]) => serviceId)
          })),
        analysisRequest: {
          scanTier: 'advanced_scan',
          targets: selectedTargets, // Use user-selected targets from UI
          selectedProviders: selectedServices, // Multi-provider selections
          analysisDepth: advancedScanConfig.analysis_depth,
          maxCostLimit: advancedScanConfig.max_cost_limit,
          enableCrossValidation: advancedScanConfig.enable_cross_validation,
          enableConfidenceScoring: advancedScanConfig.enable_confidence_scoring,
          // prefer explicit provider selection; omit preferredService unless the user chose a single service
          preferredService: undefined,
          customInstructions: `Advanced multi-provider analysis requested by ${displayName}. Focus on: ${selectedTargets.join(', ')}. Cross-validate results between providers for maximum accuracy.`
        },
        requestedBy: displayName,
        orderName: `${advancedScanConfig.name} - ${new Date().toLocaleDateString()}`
      };

      log.debug('🚀 Creating analysis order:', orderConfig);

      // Call cloud function to create order
      const createOrder = httpsCallable(functions, 'createAnalysisOrder');
      const result = await createOrder(orderConfig);

      log.info('✅ Order created:', result.data);

      // Store order ID and show results modal
      setCreatedOrderId(result.data.orderId);
      setShowOrderResults(true);
      
      // Close this modal
      onClose();
      
      // Notify parent
      if (onAnalysisComplete) {
        onAnalysisComplete(result.data.orderId);
      }

    } catch (error) {
      log.error('❌ Error creating analysis order:', error);
      setError(error.message || 'Failed to create analysis order');
    } finally {
      setIsProcessing(false);
    }
  }, [casefileId, evidence, selectedServices, selectedModels, selectedTargets, displayName, requireAuth, onClose, onAnalysisComplete]);

  if (!isOpen) return null;

  return (
    <div className="advanced-modal-overlay">
      <div className="advanced-modal">
        {/* Modal Header */}
        <div className="advanced-modal-header">
          <div className="header-content">
            <FaRobot className="header-icon" />
            <div>
              <h2>Advanced Analysis Configuration</h2>
              <p>Configure multiple providers, models, and AI services with granular control</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              className="btn-secondary"
              onClick={initializeAiServices}
              disabled={initBusy}
              title="One-time setup: seeds shared AI service catalog (Vertex, Video Intelligence)"
              style={{ padding: '6px 10px' }}
            >
              {initBusy ? 'Initializing…' : 'Initialize AI Services'}
            </button>
            <button className="close-button" onClick={onClose}>
            <FaTimes />
            </button>
          </div>
        </div>

        {/* Summary Bar */}
        <div className="summary-bar">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-value">{orderSummary.activeProviders}</span>
              <span className="stat-label">Providers</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{orderSummary.totalServices}</span>
              <span className="stat-label">Services</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">${orderSummary.totalCost.toFixed(4)}/min</span>
              <span className="stat-label">Est. Cost</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">~{Math.ceil(orderSummary.totalCost * 3)}min</span>
              <span className="stat-label">Runtime</span>
            </div>
          </div>
          {!!initMessage && (
            <div style={{
              marginLeft: '12px',
              fontSize: '12px',
              color: initMessage.toLowerCase().includes('fail') ? '#dc2626' : '#059669'
            }}>
              {initMessage}
            </div>
          )}
        </div>

        {/* Provider Tabs */}
        <div className="provider-tabs">
          {Object.values(providers).map(provider => {
            const ProviderIcon = provider.icon;
            const isActive = activeTab === provider.id;
            const hasSelections = Object.values(selectedServices[provider.id] || {}).some(Boolean);
            
            return (
              <button
                key={provider.id}
                className={`provider-tab ${isActive ? 'active' : ''} ${hasSelections ? 'has-selections' : ''} ${!provider.isActive ? 'disabled' : ''}`}
                onClick={() => provider.isActive && setActiveTab(provider.id)}
                style={{ 
                  '--provider-color': provider.color,
                  borderColor: hasSelections ? provider.color : undefined
                }}
                disabled={!provider.isActive}
              >
                <ProviderIcon className="provider-tab-icon" />
                <span>{provider.name}</span>
                {!provider.isActive && <span className="coming-soon">[Soon]</span>}
              </button>
            );
          })}
        </div>

        {/* Provider Configuration */}
        <div className="provider-config">
          {Object.values(providers).map(provider => {
            if (activeTab !== provider.id) return null;
            
            const ProviderIcon = provider.icon;
            const providerServices = selectedServices[provider.id] || {};
            const selectedModel = selectedModels[provider.id];
            
            return (
              <div key={provider.id} className="config-panel">
                <div className="provider-header">
                  <ProviderIcon className="provider-icon" style={{ color: provider.color }} />
                  <div className="provider-info">
                    <h3>{provider.name}</h3>
                    <p>{provider.description}</p>
                  </div>
                </div>

                {provider.isActive ? (
                  <>
                    {/* Analysis Targets Selection */}
                    <div className="config-section">
                      <h4>Analysis Targets</h4>
                      <div className="targets-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        {availableTargets.map(target => {
                          const TargetIcon = target.icon;
                          const isSelected = selectedTargets.includes(target.id);
                          return (
                            <div
                              key={target.id}
                              className={`target-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleTarget(target.id)}
                                                             style={{
                                 border: isSelected ? '2px solid #667eea' : '2px solid #e2e8f0',
                                 borderRadius: '8px',
                                 padding: '16px',
                                 cursor: 'pointer',
                                 transition: 'all 0.2s ease',
                                 background: isSelected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '12px',
                                 boxShadow: isSelected ? '0 8px 32px rgba(102, 126, 234, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                               }}
                            >
                              <div style={{
                                background: isSelected ? 'rgba(255, 255, 255, 0.2)' : '#f1f5f9',
                                color: isSelected ? 'white' : '#64748b',
                                padding: '12px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '44px',
                                height: '44px',
                                backdropFilter: isSelected ? 'blur(10px)' : 'none'
                              }}>
                                <TargetIcon size={20} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontWeight: '600',
                                  color: isSelected ? 'white' : '#1e293b',
                                  marginBottom: '4px',
                                  textShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                }}>
                                  {target.name}
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: isSelected ? 'rgba(255, 255, 255, 0.9)' : '#64748b',
                                  marginBottom: '4px',
                                  textShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                }}>
                                  {target.description}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: isSelected ? 'rgba(255, 255, 255, 0.8)' : '#667eea',
                                  fontWeight: '500',
                                  textShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                }}>
                                  {target.cost}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Model Selection */}
                    <div className="config-section">
                      <h4>AI Models</h4>
                      <div className="models-grid">
                        {provider.models.map(model => (
                          <div
                            key={model.id}
                            className={`model-card ${selectedModel === model.id ? 'selected' : ''}`}
                            onClick={() => selectModel(provider.id, model.id)}
                          >
                            <div className="model-name">{model.name}</div>
                            <div className="model-stats">
                              <span className="model-cost">${model.cost}/1k tokens</span>
                              <span className="model-speed">{model.speed}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Services Selection */}
                    <div className="config-section">
                      <h4>AI Services</h4>
                      {provider.serviceCategories ? (
                        provider.serviceCategories.map(category => (
                          <div key={category.id} className="service-category" style={{ marginBottom: '24px' }}>
                            {/* Category Header */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '12px',
                              padding: '12px 16px',
                              background: '#f8fafc',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{
                                background: provider.color,
                                color: 'white',
                                padding: '8px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {category.icon === 'FaEye' ? <FaEye size={16} /> : <FaMicrophone size={16} />}
                              </div>
                              <div>
                                <h5 style={{ margin: 0, color: '#1e293b', fontSize: '14px', fontWeight: '600' }}>
                                  {category.name}
                                </h5>
                                <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                                  {category.description}
                                </p>
                              </div>
                              <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#64748b' }}>
                                {category.services.filter(service => providerServices[service.id]).length} of {category.services.length} services selected
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                                ${category.services.filter(service => providerServices[service.id]).reduce((sum, service) => sum + service.cost, 0).toFixed(4)}/min
                              </div>
                            </div>
                            
                            {/* Services Grid */}
                            <div className="services-grid" style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                              gap: '12px'
                            }}>
                              {category.services.map(service => {
                                const isSelected = providerServices[service.id];
                                return (
                                  <div
                                    key={service.id}
                                    className={`service-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => toggleService(provider.id, service.id)}
                                    style={{
                                      border: isSelected ? `2px solid ${provider.color}` : '2px solid #e2e8f0',
                                      borderRadius: '8px',
                                      padding: '16px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      background: isSelected ? `${provider.color}15` : '#fff'
                                    }}
                                  >
                                    <div className="service-header" style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: '8px'
                                    }}>
                                      <span className="service-name" style={{
                                        fontWeight: '600',
                                        color: '#1e293b',
                                        fontSize: '14px'
                                      }}>{service.name}</span>
                                      <span className="service-cost" style={{
                                        color: provider.color,
                                        fontWeight: '500',
                                        fontSize: '12px'
                                      }}>${service.cost}/min</span>
                                    </div>
                                    <p className="service-description" style={{
                                      margin: 0,
                                      color: '#64748b',
                                      fontSize: '12px',
                                      lineHeight: '1.4'
                                    }}>{service.description}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      ) : (
                        // Fallback for providers without categorized services
                        <div className="services-grid">
                          {provider.services?.map(service => {
                            const isSelected = providerServices[service.id];
                            return (
                              <div
                                key={service.id}
                                className={`service-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => toggleService(provider.id, service.id)}
                              >
                                <div className="service-header">
                                  <span className="service-name">{service.name}</span>
                                  <span className="service-cost">${service.cost}/min</span>
                                </div>
                                <p className="service-description">{service.description}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="placeholder-content">
                    <FaExclamationTriangle className="placeholder-icon" />
                    <h4>Integration Coming Soon</h4>
                    <p>This AI provider will be available in a future update. Stay tuned for enhanced multi-provider analysis capabilities!</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* Modal Footer */}
        <div className="advanced-modal-footer">
          <div className="footer-info" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>Total estimated cost: ${orderSummary.totalCost.toFixed(4)}/min</span>
            {!validation.valid && (
              <span style={{ color: '#b45309', fontSize: '12px', fontWeight: 600 }}>
                ⚠️ {validation.reasons.length} issue{validation.reasons.length !== 1 ? 's' : ''} detected. Review before launching.
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn-tertiary" disabled>
              <FaSave /> Save Template
            </button>
            <button 
              className="btn-primary"
              onClick={createAnalysisOrder}
              disabled={isProcessing || orderSummary.totalServices === 0}
            >
              <FaRocket />
              {isProcessing ? 'Launching Advanced Analysis...' : `Launch Advanced Analysis (${orderSummary.totalServices} services)`}
            </button>
          </div>
        </div>

        {/* Validation details list */}
        {!validation.valid && (
          <div style={{
            marginTop: '10px',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            color: '#7c2d12',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: '12px'
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Issues detected</div>
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {validation.reasons.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
            <div style={{ marginTop: 6, opacity: 0.8 }}>
              You can still attempt to launch; the server will block any invalid configuration with zero cost.
            </div>
          </div>
        )}
      </div>

      {/* Order Results Modal */}
      <SingleOrderResultsModal
        isOpen={showOrderResults}
        onClose={() => {
          setShowOrderResults(false);
          setCreatedOrderId(null);
        }}
        orderId={createdOrderId}
        origin="analysis"
        casefileId={casefileId}
        casefileName={casefileName}
        evidenceId={evidence?.id}
        evidenceName={evidence?.fileName}
        currentUser={user}
      />
    </div>
  );
};

export default AdvancedAnalysisModal;